/**
 * Class Detail Page
 * View and manage individual class
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { apiRequest } from '@/api/client';
import { generateClassCredentialsHTML, downloadPDF, printHTML } from '@/utils/credentialDocument';

interface ClassData {
  classId: string;
  className: string;
  gradeLevel: string;
  classCode: string;
  teacherName: string;
  studentCount: number;
  ageRating: string;
  createdAt: string;
}

export const ClassDetailPage = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [wordLists, setWordLists] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (classId) {
      fetchClassData();
    }
  }, [classId]);

  const fetchClassData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch classes - use get_school_classes for admins, get_teacher_classes for teachers
      const action = user?.isAdmin ? 'get_school_classes' : 'get_teacher_classes';
      const params = user?.isAdmin 
        ? { schoolId: user?.schoolId }
        : { userEmail: user?.email };
      
      const classesResponse = await apiRequest<{ classes: any[] }>(action, params);
      
      if (classesResponse.success && (classesResponse as any).classes) {
        const foundClass = (classesResponse as any).classes.find((c: any) => c.classId === classId);
        if (foundClass) {
          setClassData(foundClass);
        }
      }

      // Fetch students
      const studentsResponse = await apiRequest<{ students: any[] }>('get_class_students', {
        classId,
      });
      
      if (studentsResponse.success && (studentsResponse as any).students) {
        setStudents((studentsResponse as any).students);
      }

      // Fetch word lists
      const wordListsResponse = await apiRequest<{ wordLists: any[] }>('get_class_wordlists', {
        classId,
      });
      
      if (import.meta.env.DEV) {
        console.log('Word lists response:', wordListsResponse);
      }
      
      if (wordListsResponse.success && (wordListsResponse as any).wordLists) {
        setWordLists((wordListsResponse as any).wordLists);
      }
    } catch (error) {
      console.error('Failed to fetch class data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAllCredentials = async () => {
    if (!classData || students.length === 0) {
      showNotification('error', 'No students in this class');
      return;
    }

    const savedSettings = localStorage.getItem('schoolSettings');
    const schoolSettings = savedSettings ? JSON.parse(savedSettings) : {
      schoolName: user?.schoolName || 'School',
      schoolLogo: '',
      schoolAddress: '',
      schoolPhone: '',
      schoolEmail: '',
    };

    const studentCredentials = students.map(student => ({
      firstName: student.firstName,
      lastName: student.lastName,
      username: student.username,
      password: student.password || 'Not available',
      gradeName: classData.gradeLevel,
    }));

    const html = await generateClassCredentialsHTML(
      studentCredentials,
      `${user?.firstName} ${user?.lastName}`,
      schoolSettings,
      classData.gradeLevel
    );

    await downloadPDF(html, `Grade_${classData.gradeLevel}_all_credentials.pdf`);
    showNotification('success', `Downloaded credentials for ${students.length} students`);
  };

  const handlePrintAllCredentials = async () => {
    if (!classData || students.length === 0) {
      showNotification('error', 'No students in this class');
      return;
    }

    const savedSettings = localStorage.getItem('schoolSettings');
    const schoolSettings = savedSettings ? JSON.parse(savedSettings) : {
      schoolName: user?.schoolName || 'School',
      schoolLogo: '',
      schoolAddress: '',
      schoolPhone: '',
      schoolEmail: '',
    };

    const studentCredentials = students.map(student => ({
      firstName: student.firstName,
      lastName: student.lastName,
      username: student.username,
      password: student.password || 'Not available',
      gradeName: classData.gradeLevel,
    }));

    const html = await generateClassCredentialsHTML(
      studentCredentials,
      `${user?.firstName} ${user?.lastName}`,
      schoolSettings,
      classData.gradeLevel
    );

    printHTML(html);
  };

  const handleDeleteClass = async () => {
    try {
      setIsDeleting(true);
      
      if (import.meta.env.DEV) {
        console.log('Deleting class with params:', {
          userEmail: user?.email,
          classId: classId,
        });
      }
      
      const response = await apiRequest('delete_class', {
        userEmail: user?.email,
        classId: classId,
      });

      if (response.success) {
        showNotification('success', 'Class deleted successfully');
        navigate('/classes');
      } else {
        showNotification('error', response.error || 'Failed to delete class');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to delete class:', error);
      }
      showNotification('error', 'Failed to delete class');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Class not found
            </h3>
            <p className="text-slate-600 mb-6">
              This class may have been deleted or you don't have permission to view it.
            </p>
            <Button variant="primary" onClick={() => navigate('/classes')}>
              Back to Classes
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const activeWordList = wordLists.find(wl => wl.isActive);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{classData.className}</h1>
          <p className="text-slate-600 mt-2">Year {classData.gradeLevel} • Class Code: {classData.classCode}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/classes')}>
            ← Back to Classes
          </Button>
          <Button variant="primary" onClick={() => navigate(`/classes/${classId}/edit`)}>
            Edit Class
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteDialog(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteClass}
        title="Delete Class"
        message={`Are you sure you want to delete "${classData.className}"? This will also remove all ${students.length} students and ${wordLists.length} word lists associated with this class. This action cannot be undone.`}
        confirmText="Delete Class"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="p-6">
            <div className="text-3xl font-bold text-slate-900 mb-2">{students.length}</div>
            <div className="text-sm text-slate-600">Students</div>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <div className="text-3xl font-bold text-slate-900 mb-2">{wordLists.length}</div>
            <div className="text-sm text-slate-600">Word Lists</div>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <div className="text-3xl font-bold text-slate-900 mb-2">
              {students.reduce((sum, s) => sum + (s.storiesCreated || 0), 0)}
            </div>
            <div className="text-sm text-slate-600">Stories Created</div>
          </div>
        </Card>
      </div>

      {/* Active Word List */}
      {activeWordList && (
        <Card title="Active Word List">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{activeWordList.name}</h3>
                <p className="text-sm text-slate-600">{activeWordList.words.length} words</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/wordlists/${activeWordList.wordListId}`)}>
                View Details
              </Button>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-700">
                {activeWordList.words.slice(0, 10).join(', ')}
                {activeWordList.words.length > 10 && '...'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Students */}
      <Card title={`Students (${students.length})`}>
        <div className="p-6">
          {students.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-600 mb-4">No students in this class yet</p>
              <Button variant="primary" onClick={() => navigate('/students/new')}>
                Add Students
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-3 mb-4">
                <Button 
                  variant="primary" 
                  onClick={handleDownloadAllCredentials}
                  className="flex-1"
                >
                  📄 Download All Credentials
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handlePrintAllCredentials}
                  className="flex-1"
                >
                  🖨️ Print All Credentials
                </Button>
              </div>
              <p className="text-sm text-slate-500 mb-4 text-center">
                Credentials include usernames and passwords for all students in this class.
              </p>
              <div className="space-y-3">
              {students.map((student) => (
                <div
                  key={student.studentId}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">
                        {student.firstName[0]}{student.lastName[0]}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {student.firstName} {student.lastName}
                      </div>
                      <div className="text-sm text-slate-600">{student.username}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-slate-600">
                      {student.storiesCreated || 0} stories
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/students/${student.studentId}`)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </Card>

      {/* Word Lists */}
      <Card title={`Word Lists (${wordLists.length})`}>
        <div className="p-6">
          {wordLists.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-600 mb-4">No word lists for this class yet</p>
              <Button variant="primary" onClick={() => navigate('/wordlists/new')}>
                Create Word List
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {wordLists.map((wordList) => (
                <div
                  key={wordList.wordListId}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900">{wordList.name}</div>
                      {wordList.isActive && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">{wordList.words.length} words</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/wordlists/${wordList.wordListId}`)}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
